import axios from "axios";
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from "libphonenumber-js";

// Default country for parsing numbers without a leading '+'
const DEFAULT_COUNTRY = "LK";

/**
 * ==============================================
 * PHONE NUMBER VALIDATION SERVICE
 * ==============================================
 */

/**
 * Normalizes phone number to E.164 format (+CCNNNNNNNNN).
 */
const NormalizePhone = (phone) => {
  if (!phone || typeof phone !== "string") return null;

  try {
    const phoneNumber = parsePhoneNumberFromString(
      phone.trim(),
      DEFAULT_COUNTRY
    );

    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.number;
    }

    return null;
  } catch (error) {
    console.warn("Phone normalization error:", error.message);
    return null;
  }
};

/**
 * Validates if a phone number is a valid, recognized number.
 */

//Returns TRUE on success, or an ERROR MESSAGE STRING on failure.
const ValidatePhone = (phone) => {
  if (!phone || typeof phone !== "string" || phone.trim() === "") {
    return "Valid phone number is required."; // Explicit error message
  }

  try {
    if (isValidPhoneNumber(phone)) {
      return true; // Success
    }

    // If isValidPhoneNumber returns false, return a message
    return "Invalid phone number format or length.";
  } catch (error) {
    console.warn("Phone validation error:", error.message);
    return "Could not validate phone number. Please check format.";
  }
};

// ----------------------------------------------------
// *** REVISED CLASSIFICATION FUNCTIONS (More Robust) ***
// ----------------------------------------------------

// Define all known MOBILE types/prefixes for Sri Lanka
const SL_MOBILE_TYPES = ["MOBILE", "FIXED_LINE_OR_MOBILE"];

/**
 * Checks if phone is a Sri Lankan Mobile number, or an unclassified valid SL number.
 * This is the primary authority for the 'send SMS/Email' route.
 */
const isSLMobile = (phone) => {
  if (!phone || !ValidatePhone(phone)) return false;

  try {
    const phoneNumber = parsePhoneNumberFromString(phone);

    // 1. Must be a Sri Lankan number
    if (phoneNumber.country !== "LK") return false;

    const type = phoneNumber.getType();

    // console.log(`DEBUG (isSLMobile): Number ${phone} classified as: ${type}`);

    // 2. SUCCESS CASE: If the type is explicitly MOBILE or FIXED_LINE_OR_MOBILE
    if (SL_MOBILE_TYPES.includes(type)) {
      return true;
    }

    // *** THE ROBUST FIX ***
    // 3. FALLBACK CASE: If the type is undefined/null, but the number is LK and valid,
    //    we assume it's mobile for the purpose of OTP/SMS routing.
    if (type === undefined || type === null) {
      // You could also check if the prefix LOOKS mobile (e.g., starts with +947)
      // here for an extra layer of safety, but ValidatePhone should cover this.
      return true;
    }

    // 4. FAIL CASE: Any other known type (like FIXED_LINE)
    return false;
  } catch (error) {
    console.warn("isSLMobile check error:", error.message);
    return false;
  }
};

/**
 * Checks if phone is a Sri Lankan landline.
 * This function is now only used to identify the *alternative* SL type.
 */
const isSLLandline = (phone) => {
  if (!phone || !ValidatePhone(phone)) return false;

  try {
    const phoneNumber = parsePhoneNumberFromString(phone);

    // 1. Must be a Sri Lankan number
    if (phoneNumber.country !== "LK") return false;

    const type = phoneNumber.getType();

    // 2. Must be an explicit landline type
    if (type === "FIXED_LINE") {
      // It must NOT be a number that is simultaneously considered a mobile number by our rules
      return !isSLMobile(phone);
    }

    return false;
  } catch (error) {
    console.warn("isSLLandline check error:", error.message);
    return false;
  }
};

/**
 * Checks if phone is an international number (non-Sri Lankan).
 */
const isInternational = (phone) => {
  if (!phone || !ValidatePhone(phone)) return false;

  try {
    const phoneNumber = parsePhoneNumberFromString(phone);
    // If valid, check if the country code is NOT the default country 'LK'
    return !!phoneNumber.country && phoneNumber.country !== DEFAULT_COUNTRY;
  } catch (error) {
    console.warn("International check error:", error.message);
    return false;
  }
};

/**
 * ==============================================
 * EMAIL VALIDATION SERVICE (Standard implementation)
 * ==============================================
 */

/**
 * Normalizes email address by trimming and converting to lowercase.
 */
const NormalizeEmail = (email) => {
  if (!email || typeof email !== "string") return null;

  const trimmed = email.trim().toLowerCase();

  // Basic format check before returning
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed) ? trimmed : null;
};

const ValidateEmail = (email) => {
  const normalized = NormalizeEmail(email);

  if (!normalized) {
    return "Invalid email format or email is required.";
  }

  // ... existing length checks ...
  // All other checks should now return a string error message.

  const simpleRobustRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const [localPart, domain] = normalized.split("@");

  if (localPart.length > 64 || domain.length > 255 || normalized.length > 254) {
    return "Email is too long.";
  }

  if (normalized.includes("..")) {
    return "Email contains invalid characters.";
  }

  if (simpleRobustRegex.test(normalized)) {
    return true;
  }

  return "Invalid email format."; // Final fallback error message
};

//==========================================
// ======= Name Validation =================
// ==========================================

/**
 * Formats a name string (first or last name) by trimming whitespace
 * and converting it to Title Case (Capitalized Like This).
 * CRITICAL FIX: It now correctly handles initials (T. W.) and hyphenated names (Anne-Marie).
 * @param {string} name - The raw name string.
 * @returns {string} The formatted name string.
 */
const FormatName = (name) => {
  if (!name) return "";

  const trimmedName = name.trim();

  // 1. Convert everything to lowercase first for normalization.
  let formattedName = trimmedName.toLowerCase();

  // 2. Use a regular expression to capitalize the first letter of the string,
  //    and the letter following any whitespace (\s), hyphen (-), or period (.).
  // Regex breakdown:
  // (?:^|\s|\-|\.) : Non-capturing group that matches the start of the string (^),
  //                 or a whitespace (\s), or a hyphen (\-), or a period (\.).
  // [a-z]        : Matches the lowercase letter immediately following the boundary.

  formattedName = formattedName.replace(
    /(?:^|\s|\-|\.)([a-z])/g,
    (match, char) => match.toUpperCase()
  );

  // 3. Clean up extra spaces that might be introduced by the user's initial input
  //    (e.g., if they entered "t.w. harwy", the regex will capitalize, but we must
  //    re-space for the desired "T. W. Harwy" format if the service layer handles
  //    the dot spacing, but for now, the capitalization is fixed).

  // Example run with "t.w.harwey":
  // - Starts as "t.w.harwey" (lowercase)
  // - /(?:^|\s|\-|\.)([a-z])/g matches:
  //   - ^t -> Replaced with T
  //   - .w -> Replaced with .W
  //   - .h -> Replaced with .H
  // - Result: "T.W.Harwey" (Correct capitalization, now the user just needs to input the spaces)

  // To strictly enforce the "T. W. Harwy" output format, we must add spaces after periods:
  formattedName = formattedName.replace(/\.(?=[A-Z])/g, ". ");
  formattedName = formattedName.replace(/\s+/g, " "); // Collapse multiple spaces

  // Handle the case where the user entered "T. W. Harwey" but without spaces:
  // "T.W.Harwy" -> "T. W. Harwy"

  return formattedName.trim();
};
/**
 * Validates a name string (first or last name) against legal name rules.
 * Rules:
 * 1. Must contain only letters, hyphens (-), apostrophes ('), periods (for initials like T. W.), and spaces.
 * 2. Must not be empty.
 * 3. Numerics and other special characters are NOT allowed.
 * @param {string} name - The name string to validate (should be the raw user input).
 * @returns {true | string} Returns true if valid, or an error message string if invalid.
 */
const ValidateLegalName = (name) => {
  if (!name || name.trim() === "") {
    return "Name cannot be empty.";
  }

  const trimmedName = name.trim();

  // Regex breakdown:
  // ^[...]*$ : Start and end of string, allowing zero or more of the characters inside.
  // a-zA-Z : Standard letters (case-insensitive due to the nature of the validation).
  // \s : Whitespace (spaces).
  // \- : Hyphen (for double-barreled names like 'Anne-Marie').
  // \' : Apostrophe (for names like O'Malley).
  // \. : Period (for initials like 'T.').
  const nameRegex = /^[a-zA-Z\s\-\'\.]+$/;

  if (!nameRegex.test(trimmedName)) {
    return "Name contains invalid characters. Only letters, spaces, hyphens, apostrophes, and periods (for initials) are allowed. Numerics are not allowed.";
  }

  // Prevent names consisting only of periods or spaces (e.g., ".")
  if (trimmedName.replace(/[\s\.\-\']/g, "").length === 0) {
    return "Name must contain at least one letter.";
  }

  return true; // Valid
};

// ===============================================
// ========= Address Validations =================
// ===============================================

/**
 * Validates a standard address string.
 * Rules:
 * 1. Must not be empty.
 * 2. Must be at least 5 characters long (e.g., "Main St").
 * 3. Allows letters, numbers, spaces, commas, periods, hyphens, and hashtags.
 * This covers most common address formats (e.g., "Apt #12, 123 Main St, Dehiwala").
 * @param {string} address - The address string to validate.
 * @returns {true | string} Returns true if valid, or an error message string if invalid.
 */
const ValidateAddress = (address) => {
  if (!address || address.trim() === "") {
    return "Address is required.";
  }

  const trimmedAddress = address.trim();

  // if (trimmedAddress.length < 5) {
  //   return "Address must be at least 5 characters long.";
  // }

  // Regex breakdown:
  // ^[...]*$ : Start and end of string.
  // a-zA-Z0-9 : Letters and numbers.
  // \s : Spaces.
  // ,\.#\- : Comma, period, hashtag, and hyphen.
  const addressRegex = /^[a-zA-Z0-9\s,\.\:\;\#\-]+$/;

  // if (!addressRegex.test(trimmedAddress)) {
  //   return "Address contains invalid characters. Please use only letters, numbers, spaces, commas (,), periods (.), hyphens (-), or hashtags (#).";
  // }

  return true; // Valid
};

/**
 * @private
 * Internal function to call the SL user validation API.
 */
const validateRegisteredSLUser = async (email, phone) => {

try {
  // fix this in backend.
  // normalizePhone receives with +94, backend api ask '0'.
  const formatPhone = phone.replace(/^\+94/, "");
  const formattedPhone = "0" + formatPhone;

  const res = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}user/validate/sluser`,
    { email, phoneNumber: formattedPhone }
  );
  return res?.data?.data;
} catch (err) {
    // ✅ Extract message from backend JSON response safely
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to validate contact details.";
    throw new Error(message);
  }
};

/**
 * @private
 * Internal function to call the International user validation API.
 */
const validateRegisteredInternationalUser = async (email, phone) => {
try {
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}user/validate/intuser`,
      { email, phoneNumber: phone }
    );
    return res?.data?.data;
}
  catch (err) {
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to validate contact details.";
    throw new Error(message);
  }
};


const validateRegisteredUser = (email, phone) => {
   const isSLPhone = isSLMobile(phone) || isSLLandline(phone);

   const endpoint = isSLPhone
     ? validateRegisteredSLUser(email, phone)
     : validateRegisteredInternationalUser(email, phone);

  return endpoint;
}

export {
  NormalizePhone,
  NormalizeEmail,
  ValidatePhone,
  ValidateEmail,
  isSLMobile,
  isSLLandline,
  isInternational,
  FormatName,
  ValidateLegalName,
  ValidateAddress,
  validateRegisteredSLUser,
  validateRegisteredInternationalUser,
  validateRegisteredUser,
};
